import { GridRow } from '../../../types/types';
import { resolveOptionalNumber } from './resolveOptionalNumber';
import { ReportAggregateOperation } from '../types';

/**
 * Aggregates one column over the given rows. Rows must be the complete result
 * set, not the visible page — a footer total over a paged grid would be quietly
 * wrong. Non-numeric and empty values are skipped rather than counted as zero,
 * so an average is not dragged down by blanks.
 */
export const computeAggregate = (
  rows: GridRow[],
  operation: ReportAggregateOperation,
  itemName?: string
): number | undefined => {
  if (operation === 'count') {
    return rows.length;
  }

  if (!itemName) {
    return undefined;
  }

  const values = rows
    .map((row) => resolveOptionalNumber(row[itemName]))
    .filter((value): value is number => value !== undefined);

  if (values.length === 0) {
    // The sum of nothing is zero, and a footer line that prints nothing at all
    // reads as broken. An average, minimum or maximum of nothing has no value,
    // so those stay blank rather than claiming a zero nobody computed.
    return operation === 'sum' ? 0 : undefined;
  }

  switch (operation) {
    case 'sum':
      return values.reduce((total, value) => total + value, 0);
    case 'avg':
      return values.reduce((total, value) => total + value, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return undefined;
  }
};
